import React, {
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

// Video Player State Machine Types
type VideoPlayerState =
  | "idle"
  | "initializing"
  | "loading"
  | "playing"
  | "paused"
  | "ended"
  | "error"
  | "retrying"
  | "buffering"
  | "stalled";

type VideoPlayerEvent =
  | { type: "INITIALIZE"; mediaItem: MediaItem }
  | { type: "LOAD_START" }
  | { type: "LOAD_SUCCESS" }
  | { type: "LOAD_ERROR"; error: string }
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "END" }
  | { type: "RETRY" }
  | { type: "RESET" }
  | { type: "SEEK"; time: number }
  | { type: "BUFFERING" }
  | { type: "BUFFER_READY" }
  | { type: "STALLED" }
  | {
      type: "PROGRESS";
      currentTime: number;
      duration: number;
      buffered: number;
    };

interface VideoPlayerContext {
  mediaItem: MediaItem | null;
  streamUrl: string | null;
  error: string | null;
  retryCount: number;
  currentTime: number;
  duration: number;
  buffered: number;
  volume: number;
  muted: boolean;
  userMessage: string | null; // User-facing message based on state
  canInteract: boolean; // Whether user can interact with video controls
}

interface VideoPlayerStateValue {
  state: VideoPlayerState;
  context: VideoPlayerContext;
}

// Helper function to generate user messages based on state
function getUserMessage(
  state: VideoPlayerState,
  context: VideoPlayerContext,
): string | null {
  switch (state) {
    case "idle":
      return null;
    case "initializing":
      return "Preparing video...";
    case "loading":
      return "Loading video...";
    case "buffering":
      return "Buffering...";
    case "stalled":
      return "Video connection is slow. Please wait...";
    case "playing":
      return null;
    case "paused":
      return null;
    case "ended":
      return "Video finished";
    case "error":
      if (context.retryCount >= 3) {
        return "Unable to load video. Please check your connection.";
      }
      return `Loading failed. ${context.error || ""} Retrying...`;
    case "retrying":
      return `Retrying... (${context.retryCount}/3)`;
    default:
      return null;
  }
}

// Helper function to determine if user can interact
function getCanInteract(state: VideoPlayerState): boolean {
  return ["playing", "paused", "ended"].includes(state);
}

// State Machine Reducer
function videoPlayerReducer(
  current: VideoPlayerStateValue,
  event: VideoPlayerEvent,
): VideoPlayerStateValue {
  const { state, context } = current;

  switch (event.type) {
    case "INITIALIZE":
      const newContext = {
        ...context,
        mediaItem: event.mediaItem,
        error: null,
        retryCount: 0,
      };
      const newState = "initializing";
      return {
        state: newState,
        context: {
          ...newContext,
          userMessage: getUserMessage(newState, newContext),
          canInteract: getCanInteract(newState),
        },
      };

    case "LOAD_START":
      const loadingState = "loading";
      const loadingContext = {
        ...context,
        error: null,
      };
      return {
        state: loadingState,
        context: {
          ...loadingContext,
          userMessage: getUserMessage(loadingState, loadingContext),
          canInteract: getCanInteract(loadingState),
        },
      };

    case "LOAD_SUCCESS":
      // Only transition to paused if we're currently loading
      // Ignore subsequent canplay events when already in paused/playing states
      if (!["loading", "initializing", "retrying"].includes(state)) {
        return current; // No state change
      }

      const pausedState = "paused";
      const pausedContext = {
        ...context,
        error: null,
      };
      return {
        state: pausedState,
        context: {
          ...pausedContext,
          userMessage: getUserMessage(pausedState, pausedContext),
          canInteract: getCanInteract(pausedState),
        },
      };

    case "LOAD_ERROR":
      const errorState = "error";
      const errorContext = {
        ...context,
        error: event.error,
      };
      return {
        state: errorState,
        context: {
          ...errorContext,
          userMessage: getUserMessage(errorState, errorContext),
          canInteract: getCanInteract(errorState),
        },
      };

    case "BUFFERING":
      const bufferingState = "buffering";
      return {
        state: bufferingState,
        context: {
          ...context,
          userMessage: getUserMessage(bufferingState, context),
          canInteract: getCanInteract(bufferingState),
        },
      };

    case "BUFFER_READY":
      // Return to previous playing state
      const bufferReadyState = "playing";
      return {
        state: bufferReadyState,
        context: {
          ...context,
          userMessage: getUserMessage(bufferReadyState, context),
          canInteract: getCanInteract(bufferReadyState),
        },
      };

    case "STALLED":
      const stalledState = "stalled";
      return {
        state: stalledState,
        context: {
          ...context,
          userMessage: getUserMessage(stalledState, context),
          canInteract: getCanInteract(stalledState),
        },
      };

    case "PROGRESS":
      return {
        state,
        context: {
          ...context,
          currentTime: event.currentTime,
          duration: event.duration,
          buffered: event.buffered,
        },
      };

    case "PLAY":
      if (state === "paused" || state === "ended") {
        const playingState = "playing";
        return {
          state: playingState,
          context: {
            ...context,
            userMessage: getUserMessage(playingState, context),
            canInteract: getCanInteract(playingState),
          },
        };
      }
      return current;

    case "PAUSE":
      if (state === "playing") {
        const pauseState = "paused";
        return {
          state: pauseState,
          context: {
            ...context,
            userMessage: getUserMessage(pauseState, context),
            canInteract: getCanInteract(pauseState),
          },
        };
      }
      return current;

    case "END":
      const endedState = "ended";
      return {
        state: endedState,
        context: {
          ...context,
          userMessage: getUserMessage(endedState, context),
          canInteract: getCanInteract(endedState),
        },
      };

    case "RETRY":
      if (state === "error" && context.retryCount < 3) {
        const retryState = "retrying";
        const retryContext = {
          ...context,
          error: null,
          retryCount: context.retryCount + 1,
        };
        return {
          state: retryState,
          context: {
            ...retryContext,
            userMessage: getUserMessage(retryState, retryContext),
            canInteract: getCanInteract(retryState),
          },
        };
      }
      return current;

    case "RESET":
      const idleState = "idle";
      const resetContext = {
        ...context,
        mediaItem: null,
        streamUrl: null,
        error: null,
        retryCount: 0,
        currentTime: 0,
      };
      return {
        state: idleState,
        context: {
          ...resetContext,
          userMessage: getUserMessage(idleState, resetContext),
          canInteract: getCanInteract(idleState),
        },
      };

    case "SEEK":
      return {
        state,
        context: {
          ...context,
          currentTime: event.time,
        },
      };

    default:
      return current;
  }
}

// Initial State
const initialState: VideoPlayerStateValue = {
  state: "idle",
  context: {
    mediaItem: null,
    streamUrl: null,
    error: null,
    retryCount: 0,
    currentTime: 0,
    duration: 0,
    buffered: 0,
    volume: 1,
    muted: false,
    userMessage: null,
    canInteract: false,
  },
};

// Hook Options
interface UseVideoPlayerOptions {
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  autoPlay?: boolean;
  skipHeal?: boolean; // Skip autoheal for fast-path video streaming (review mode)
}

// Hook Return Type
interface UseVideoPlayerReturn {
  // State machine
  state: VideoPlayerState;
  context: VideoPlayerContext;

  // Actions
  initialize: (mediaItem: MediaItem) => void;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  retry: () => void;
  reset: () => void;

  // Video element
  videoRef: React.RefObject<HTMLVideoElement>;

  // Computed state booleans for UI
  isLoading: boolean;
  isPlaying: boolean;
  isPaused: boolean;
  hasError: boolean;
  canRetry: boolean;
  showSpinner: boolean;
  showControls: boolean;

  // User communication
  userMessage: string | null;

  // Stream URL
  streamUrl: string | null;
}

export function useVideoPlayer(
  options: UseVideoPlayerOptions = {},
): UseVideoPlayerReturn {
  const {
    getStreamUrlByFileId: externalGetStreamUrl,
    autoPlay = true,
    skipHeal = false,
  } = options;

  const [current, dispatch] = useReducer(
    (state: VideoPlayerStateValue, action: VideoPlayerEvent) => {
      const newState = videoPlayerReducer(state, action);
      // Only log important state transitions to reduce noise
      return newState;
    },
    initialState,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentBlobUrl = useRef<string | null>(null);

  // Generate stream URL when media item changes
  const streamUrl = useMemo(() => {
    const { mediaItem } = current.context;
    if (!mediaItem) return null;

    // If custom URL function provided, use it
    if (externalGetStreamUrl) {
      const customUrl = externalGetStreamUrl(
        mediaItem.fileIdentity,
      );
      return customUrl || null;
    }

    // Otherwise use MediaItem's stream URL with access_token and optional skip_heal
    return mediaItem.getStreamUrlWithQueryAuth(skipHeal);
  }, [
    current.context.mediaItem,
    externalGetStreamUrl,
    skipHeal,
  ]);

  // Actions - only dispatch events, side effects handled in useEffects
  const initialize = useCallback((mediaItem: MediaItem) => {
    dispatch({ type: "INITIALIZE", mediaItem });
  }, []);

  const play = useCallback(() => {
    dispatch({ type: "PLAY" });
  }, []);

  const pause = useCallback(() => {
    dispatch({ type: "PAUSE" });
  }, []);

  const seek = useCallback((time: number) => {
    dispatch({ type: "SEEK", time });
  }, []);

  const retry = useCallback(() => {
    dispatch({ type: "RETRY" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // State-driven effects - each useEffect watches for specific states and triggers next transitions

  // Effect: Set up video element event listeners
  // Re-runs when media item changes to handle conditionally rendered video elements
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Only handle essential state transitions - no redundant events
    const handleCanPlay = () => dispatch({ type: "LOAD_SUCCESS" });
    const handleError = () => {
      const error = video.error?.message || "Video loading failed";
      dispatch({ type: "LOAD_ERROR", error });
    };

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);
    };
  }, [current.context.mediaItem]);

  // Effect: Handle 'initializing' state - start loading immediately
  useEffect(() => {
    if (current.state === "initializing") {
      const { mediaItem } = current.context;

      // If we have media item and stream URL, start loading
      if (mediaItem && streamUrl) {
        dispatch({ type: "LOAD_START" });
      } else {
        if (mediaItem && !streamUrl) {
          dispatch({
            type: "LOAD_ERROR",
            error: "No stream URL available for media item",
          });
        }
      }
    }
  }, [
    current.state,
    current.context.mediaItem,
    streamUrl,
  ]);

  // Track if we've already set the video source to avoid re-loading
  const videoSourceSet = useRef(false);

  // Effect: Handle 'loading' state - resolve signed URL then set video source
  useEffect(() => {
    if (current.state === "loading" && !videoSourceSet.current) {
      if (!videoRef.current) {
        return;
      }

      if (!streamUrl) {
        console.error("No stream URL available in loading state");
        dispatch({ type: "LOAD_ERROR", error: "No stream URL available" });
        return;
      }

      // Cleanup previous blob URL
      if (currentBlobUrl.current) {
        URL.revokeObjectURL(currentBlobUrl.current);
        currentBlobUrl.current = null;
      }

      // Mark source as set immediately to prevent duplicate fetches
      videoSourceSet.current = true;

      const video = videoRef.current;
      video.setAttribute('x-webkit-airplay', 'allow');
      video.preload = "auto";

      // Resolve the signed URL from the backend, then set it on
      // the video element directly. This avoids a cross-origin
      // 302 redirect chain (backend -> GCS) which browsers block.
      const resolveAndSetSource = async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          // Request JSON response so backend returns {"url": signedGcsUrl}
          // instead of a 302 redirect (which browsers follow, returning bytes)
          const jsonUrl = streamUrl + (streamUrl.includes("?") ? "&" : "?") + "format=json";
          console.log("[VideoPlayer] Fetching signed URL from:", jsonUrl);
          const response = await fetch(jsonUrl, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!response.ok) {
            const body = await response.text().catch(() => "(unreadable)");
            console.error(`[VideoPlayer] Backend returned ${response.status}:`, body);
            dispatch({
              type: "LOAD_ERROR",
              error: `Failed to get video URL (${response.status})`,
            });
            videoSourceSet.current = false;
            return;
          }
          const data = await response.json();
          if (!data.url) {
            console.error("[VideoPlayer] Backend response missing 'url' field:", data);
            dispatch({ type: "LOAD_ERROR", error: "No video URL returned" });
            videoSourceSet.current = false;
            return;
          }
          console.log("[VideoPlayer] Setting video.src to signed GCS URL");
          video.src = data.url;
          // Mobile browsers (iOS Safari, Android Chrome) evaluate the
          // declarative `autoplay` attribute when the element mounts.
          // Because we assign `src` later, that initial evaluation runs
          // against an empty source and never re-triggers. Call play()
          // imperatively now that the source is set; swallow the
          // rejection so a blocked autoplay falls back to the visible
          // controls instead of throwing.
          if (autoPlay) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch((err) => {
                console.warn("[VideoPlayer] Autoplay rejected:", err);
              });
            }
          }
        } catch (err) {
          const message = err instanceof Error && err.name === "AbortError"
            ? "Video request timed out"
            : err instanceof Error ? err.message : "Network error";
          console.error("[VideoPlayer] resolveAndSetSource failed:", message, err);
          dispatch({ type: "LOAD_ERROR", error: message });
          videoSourceSet.current = false;
        }
      };

      resolveAndSetSource();
    }
  }, [current.state, streamUrl]);

  // Reset the video source flag when media item changes
  useEffect(() => {
    videoSourceSet.current = false;
  }, [current.context.mediaItem]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (currentBlobUrl.current) {
        URL.revokeObjectURL(currentBlobUrl.current);
      }
    };
  }, []);

  // Let the video element handle its own playback - no state-driven effects needed

  // Delay showing spinner to avoid flash on cached videos
  const [showSpinnerDelayed, setShowSpinnerDelayed] = React.useState(false);
  const spinnerTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const shouldShowSpinner = [
      "loading",
      "initializing",
      "buffering",
    ].includes(current.state);

    if (shouldShowSpinner) {
      // Delay spinner by 300ms - cached videos load faster than this
      spinnerTimeoutRef.current = window.setTimeout(() => {
        setShowSpinnerDelayed(true);
      }, 300);
    } else {
      // Clear timeout and hide spinner immediately when loaded
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
        spinnerTimeoutRef.current = null;
      }
      setShowSpinnerDelayed(false);
    }

    return () => {
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    };
  }, [current.state]);

  // Computed properties for UI
  const isLoading = [
    "loading",
    "initializing",
    "retrying",
    "buffering",
  ].includes(current.state);
  const isPlaying = current.state === "playing";
  const isPaused = current.state === "paused";
  const hasError = current.state === "error";
  const canRetry = hasError && current.context.retryCount < 3;
  const showSpinner = showSpinnerDelayed; // Use delayed version
  const showControls = current.context.canInteract;
  const userMessage = current.context.userMessage;

  return {
    // State machine
    state: current.state,
    context: current.context,

    // Actions
    initialize,
    play,
    pause,
    seek,
    retry,
    reset,

    // Video element
    videoRef,

    // Computed state booleans for UI
    isLoading,
    isPlaying,
    isPaused,
    hasError,
    canRetry,
    showSpinner,
    showControls,

    // User communication
    userMessage,

    // Stream URL
    streamUrl,
  };
}

